import type { ReactNode } from "react";

import type { Insight as InsightData } from "../domain/insight";
import { useGo } from "../lib/useGo";
import { figureText } from "./Hero";
import { I } from "./icons";
import { Insight } from "./Insight";

export interface ChapterLead {
  /** the StatStrip idiom, at 28px rather than the page Hero's 48 (kit §24: only a Hero is 48) */
  figure: ReactNode | null;
  unit?: ReactNode;
  label: ReactNode;
  note?: ReactNode;
}

/**
 * One section of the Progress profile (kit §24, ADR-033): a heading naming the focused page it
 * links to, then ONE group holding one or two lead figures and one to three Insight rows. No
 * chart, no list here — those live on the focused page `to` points at. Cycle context belongs in
 * an Insight's sentence or its `cycle` Tag, never as its own row.
 *
 * Every Insight in a Chapter carries a border-top, including the first, because the leads sit
 * above it — contrast a standalone `InsightList`, whose first row is flush.
 */
export function Chapter({
  title,
  to,
  linkLabel,
  leads,
  insights,
}: {
  title: ReactNode;
  to: string;
  linkLabel: ReactNode;
  leads: ChapterLead[];
  insights: InsightData[];
}) {
  const navigate = useGo();
  return (
    <section className="k-chapter">
      <div className="k-chapter__head">
        <h2 className="k-chapter__h">{title}</h2>
        <button type="button" className="k-chapter__link" onClick={() => navigate(to)}>
          {linkLabel}
          {I["chevron-right"]}
        </button>
      </div>
      <div className="k-rows">
        <div className={`k-chapter__leads${leads.length > 1 ? " k-chapter__leads--split" : ""}`}>
          {leads.map((lead, i) => (
            <div className="k-chapter__lead" key={i}>
              <span className={`k-chapter__leadfig${lead.figure === null ? " is-empty" : ""}`}>
                {figureText(lead.figure)}
                {lead.unit && lead.figure !== null ? <span className="k-chapter__leadunit">{lead.unit}</span> : null}
              </span>
              <span className="k-chapter__leadlabel">{lead.label}</span>
              {lead.note !== undefined ? <span className="k-chapter__leadnote">{lead.note}</span> : null}
            </div>
          ))}
        </div>
        {insights.map((insight) => (
          <Insight key={insight.key} insight={insight} />
        ))}
      </div>
    </section>
  );
}
